import React, { Component, createRef } from 'react';
import { connect } from 'react-redux'
import { AppState } from '../store'
import { updateMilestoneAction } from '../store/milestones/actions';
import { updateSubWorkflowAction } from '../store/subworkflows/actions';
import { updateWorkflowAction } from '../store/workflows/actions';
import { updateFeatureAction } from '../store/features/actions';
import { updateProjectAction } from '../store/projects/actions';
import { Formik, FormikHelpers as FormikActions, FormikProps, Form, Field, FieldProps } from 'formik';
import { API_UPDATE_MILESTONE_DESCRIPTION, API_UPDATE_PROJECT_DESCRIPTION } from "../api";
import { API_UPDATE_SUBWORKFLOW_DESCRIPTION } from "../api";
import { API_UPDATE_WORKFLOW_DESCRIPTION } from "../api";
import { API_UPDATE_FEATURE_DESCRIPTION } from "../api";
import * as Yup from 'yup';
import { IMilestone } from '../store/milestones/types';
import { application } from '../store/application/selectors';
import { IApplication } from '../store/application/types';
import { ISubWorkflow } from '../store/subworkflows/types';
import { EntityTypes } from '../core/card'
import { IWorkflow } from '../store/workflows/types';
import { IFeature } from '../store/features/types';
import onClickOutside from "react-onclickoutside";
import ReactMarkdown from 'react-markdown'
import { Button } from './elements';
import { IProject } from '../store/projects/types';

const mapStateToProps = (state: AppState) => ({
    application: application(state)
})

const mapDispatchToProps = {
    updateMilestone: updateMilestoneAction,
    updateSubWorkflow: updateSubWorkflowAction,
    updateWorkflow: updateWorkflowAction,
    updateFeature: updateFeatureAction,
    updateProject: updateProjectAction,
}

interface PropsFromState {
    application: IApplication
}

interface PropsFromDispatch {
    updateMilestone: typeof updateMilestoneAction
    updateSubWorkflow: typeof updateSubWorkflowAction
    updateWorkflow: typeof updateWorkflowAction
    updateFeature: typeof updateFeatureAction
    updateProject: typeof updateProjectAction
}


interface SelfProps {
    entity: EntityTypes
    app: IApplication
    url: string
    close: () => void
    viewOnly: boolean
    demo: boolean
}
type Props = PropsFromState & PropsFromDispatch & SelfProps


interface State {
    edit: boolean
    uploading: boolean
}


class EntityDetailsDescription extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.submitForm = () => { }
        this.state = { edit: false, uploading: false }
    }

    // ref 用于获取 textarea 的光标位置
    textareaRef = createRef<HTMLTextAreaElement>()

    handleClickOutside = () => {
        if (this.state.edit) {
            this.setState({ edit: false })
            this.submitForm()
        }
    }

    submitForm: () => void;

    // 在光标处插入文本，并更新 Formik 的值
    insertAtCursor = (setFieldValue: (field: string, value: string) => void, current: string, text: string) => {
        const ta = this.textareaRef.current
        if (!ta) {
            setFieldValue('description', current + text)
            return
        }
        const start = ta.selectionStart
        const end = ta.selectionEnd
        const next = current.slice(0, start) + text + current.slice(end)
        setFieldValue('description', next)
        // 下一帧把光标移到插入内容之后
        requestAnimationFrame(() => {
            ta.selectionStart = ta.selectionEnd = start + text.length
            ta.focus()
        })
    }

    // 处理粘贴事件：检测图片，上传后插入 Markdown 链接
    handlePaste = (
        e: React.ClipboardEvent<HTMLTextAreaElement>,
        currentValue: string,
        setFieldValue: (field: string, value: string) => void
    ) => {
        const items = Array.from(e.clipboardData.items)
        const imageItem = items.find(item => item.kind === 'file' && item.type.startsWith('image/'))
        if (!imageItem) return  // 普通文字粘贴正常处理

        e.preventDefault()
        const file = imageItem.getAsFile()
        if (!file) return

        this.setState({ uploading: true })

        // 先插入占位符，上传完成后替换
        const placeholder = `![uploading...]() `
        this.insertAtCursor(setFieldValue, currentValue, placeholder)

        const formData = new FormData()
        formData.append('file', file)

        fetch('/v1/uploads', { method: 'POST', body: formData, credentials: 'include' })
            .then(res => {
                if (!res.ok) throw new Error('upload failed')
                return res.json() as Promise<{ url: string }>
            })
            .then(({ url }) => {
                // 用实际 URL 替换占位符
                setFieldValue('description',
                    (this.textareaRef.current?.value ?? currentValue)
                        .replace(placeholder, `![image](${url}) `)
                )
            })
            .catch(() => {
                // 上传失败，移除占位符
                setFieldValue('description',
                    (this.textareaRef.current?.value ?? currentValue)
                        .replace(placeholder, '')
                )
                alert('图片上传失败，请重试。')
            })
            .finally(() => this.setState({ uploading: false }))
    }

    render() {
        let closed = false
        switch (this.props.entity.kind) {
            case "milestone":
            case "subworkflow":
            case "workflow":
            case "feature": {
                closed = this.props.entity.status === "CLOSED"
                break;
            }
            default:
                break;
        }

        return (
            <div className=" self-start w-full mb-4 " >

                <Formik
                    initialValues={{ description: this.props.entity.description }}

                    validationSchema={Yup.object().shape({
                        description: Yup.string()
                            .max(500000, 'Maximum 500000 characters.')
                    })}

                    onSubmit={(values: { description: string }, actions: FormikActions<{ description: string }>) => {

                        switch (this.props.entity.kind) {
                            case "project": {
                                const optimistic = this.props.entity
                                optimistic.description = values.description
                                optimistic.lastModified = new Date().toISOString()
                                optimistic.lastModifiedByName = this.props.app.account === undefined ? "demo" : this.props.app.account!.name

                                this.props.updateProject(optimistic)

                                if (!this.props.demo) {
                                    API_UPDATE_PROJECT_DESCRIPTION(this.props.entity.workspaceId, this.props.entity.id, values.description)
                                        .then(response => {
                                            if (response.ok) {
                                                response.json().then((data: IProject) => {
                                                    this.props.updateProject(data)
                                                }
                                                )
                                            } else {
                                                alert("Something went wrong when updating description.")
                                            }
                                        }
                                        )
                                }

                                break;
                            }

                            case "milestone": {
                                const optimistic = this.props.entity
                                optimistic.description = values.description
                                optimistic.lastModified = new Date().toISOString()
                                optimistic.lastModifiedByName = this.props.app.account === undefined ? "demo" : this.props.app.account!.name

                                this.props.updateMilestone(optimistic)
                                if (!this.props.demo) {
                                    API_UPDATE_MILESTONE_DESCRIPTION(this.props.entity.workspaceId, this.props.entity.id, values.description)
                                        .then(response => {
                                            if (response.ok) {
                                                response.json().then((data: IMilestone) => {
                                                    this.props.updateMilestone(data)
                                                }
                                                )
                                            } else {
                                                alert("Something went wrong when updating description.")
                                            }
                                        }
                                        )
                                }

                                break;
                            }

                            case "subworkflow": {
                                const optimistic = this.props.entity
                                optimistic.description = values.description
                                optimistic.lastModified = new Date().toISOString()
                                optimistic.lastModifiedByName = this.props.app.account === undefined ? "demo" : this.props.app.account!.name

                                this.props.updateSubWorkflow(optimistic)

                                if (!this.props.demo) {
                                    API_UPDATE_SUBWORKFLOW_DESCRIPTION(this.props.entity.workspaceId, this.props.entity.id, values.description)
                                        .then(response => {
                                            if (response.ok) {
                                                response.json().then((data: ISubWorkflow) => {
                                                    this.props.updateSubWorkflow(data)
                                                }
                                                )
                                            } else {
                                                alert("Something went wrong when updating description.")
                                            }
                                        }
                                        )
                                }

                                break;
                            }

                            case "workflow": {
                                const optimistic = this.props.entity
                                optimistic.description = values.description
                                optimistic.lastModified = new Date().toISOString()
                                optimistic.lastModifiedByName = this.props.app.account === undefined ? "demo" : this.props.app.account!.name

                                this.props.updateWorkflow(optimistic)

                                if (!this.props.demo) {
                                    API_UPDATE_WORKFLOW_DESCRIPTION(this.props.entity.workspaceId, this.props.entity.id, values.description)
                                        .then(response => {
                                            if (response.ok) {
                                                response.json().then((data: IWorkflow) => {
                                                    this.props.updateWorkflow(data)
                                                }
                                                )
                                            } else {
                                                alert("Something went wrong when updating description.")
                                            }
                                        }
                                        )
                                }

                                break;
                            }

                            case "feature": {
                                const optimistic = this.props.entity
                                optimistic.description = values.description
                                optimistic.lastModified = new Date().toISOString()
                                optimistic.lastModifiedByName = this.props.app.account === undefined ? "demo" : this.props.app.account!.name

                                this.props.updateFeature(optimistic)

                                if (!this.props.demo) {
                                    API_UPDATE_FEATURE_DESCRIPTION(this.props.entity.workspaceId, this.props.entity.id, values.description)
                                        .then(response => {
                                            if (response.ok) {
                                                response.json().then((data: IFeature) => {
                                                    this.props.updateFeature(data)
                                                }
                                                )
                                            } else {
                                                alert("Something went wrong when updating description.")
                                            }
                                        }
                                        )

                                }
                                break;
                            }

                            default:
                                break;
                        }

                        this.setState({ edit: false })
                        actions.setSubmitting(false)
                    }}
                >
                    {(formikBag: FormikProps<{ description: string }>) => {

                        this.submitForm = formikBag.submitForm

                        return (
                            <Form  >
                                {formikBag.status && formikBag.status.msg && <div>{formikBag.status.msg}</div>}

                                {this.state.edit ?
                                    <Field name="description">
                                        {({ form }: FieldProps<{ description: string }>) => (
                                            <div className="flex flex-col mt-1 ">
                                                <div className="relative">
                                                    <textarea
                                                        ref={this.textareaRef}
                                                        autoFocus
                                                        rows={20}
                                                        value={form.values.description}
                                                        onChange={form.handleChange}
                                                        onPaste={e => this.handlePaste(e, form.values.description, form.setFieldValue)}
                                                        placeholder="Description（支持 Markdown，可直接粘贴图片）"
                                                        id="description"
                                                        className="rounded p-2 border w-full"
                                                        disabled={this.state.uploading}
                                                    />
                                                    {this.state.uploading && (
                                                        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-60 rounded">
                                                            <span className="text-sm text-gray-500">图片上传中…</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <span className="text-xs right p-1">
                                                    支持 <a rel="noopener noreferrer" target="_blank" className="link" href="https://github.com/adam-p/markdown-here/wiki/Markdown-Cheatsheet">Markdown</a>，可直接 Ctrl+V 粘贴截图
                                                </span>
                                                <div className="p-1 text-red-500 text-xs font-bold">{form.touched.description && form.errors.description}</div>
                                            </div>
                                        )}
                                    </Field>
                                    :
                                    <div className=" mt-2 ml-2 border border-white  ">
                                        <div>
                                            {this.props.entity.description.length === 0 ? (
                                                <div>
                                                    <em>No description.</em>
                                                </div>)
                                                :
                                                <div>
                                                    <div className="text-left markdown-body overflow-auto">
                                                        <ReactMarkdown
                                                            children={this.props.entity.description}
                                                            linkTarget="_blank"
                                                            transformImageUri={uri => uri}
                                                        />
                                                    </div>
                                                </div>
                                            }
                                            <div className="mt-4">
                                                {!(this.props.viewOnly || closed) && <Button title="Edit description" icon="edit" handleOnClick={() => this.setState({ edit: true })} />}
                                            </div>
                                        </div>

                                    </div>
                                }
                            </Form>
                        )
                    }}
                </Formik>
            </div>)

    }
}

export default connect(mapStateToProps, mapDispatchToProps)(onClickOutside(EntityDetailsDescription))
